/**
 * One client, full width, in a tab of its own: who they are and what the FCA Register says
 * about them right now.
 *
 * This exists because the Register entry does not fit in a client card. A firm's permissions
 * and requirements are long sentences of regulatory prose, and squeezing them into a column
 * beside the scope profile made both unreadable. Popped out, the labels and their values can
 * sit side by side and the whole entry can be taken in at once.
 *
 * Reached at `#/clients/<id>`, which costs nothing to serve — a hash never reaches CloudFront,
 * so there is no rewrite rule to get wrong. Her sign-in comes with her: the refresh token is
 * in localStorage, so a new tab resumes the session rather than asking for MFA again.
 */

import { useEffect, useState } from "react";

import { api } from "../api";
import { formatDateTime } from "../config";
import type { Client, RegisterFirm } from "../types";

interface Props {
  clientId: string;
}

export function ClientPage({ clientId }: Props) {
  const [client, setClient] = useState<Client | null>(null);
  const [firm, setFirm] = useState<RegisterFirm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // The client record and the Register entry are fetched together rather than in sequence:
    // the FCA call is seven requests deep and the slower of the two by far, so waiting for
    // her own data first would add its latency to the wait for nothing.
    void Promise.allSettled([api.getClient(clientId), api.clientRegister(clientId)]).then(
      ([clientResult, firmResult]) => {
        if (cancelled) return;

        if (clientResult.status === "fulfilled") {
          setClient(clientResult.value);
        }
        if (firmResult.status === "fulfilled") {
          setFirm(firmResult.value);
        } else {
          const reason = firmResult.reason;
          setError(reason instanceof Error ? reason.message : String(reason));
        }
        setLoading(false);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Her taskbar shows a row of identical tabs otherwise.
  useEffect(() => {
    const name = client?.name ?? clientId;
    document.title = `${name} — FCA Register`;
  }, [client, clientId]);

  return (
    <div className="dossier">
      <header>
        <div className="brand">
          <img src="/norwood-logo.png" alt="Norwood Risk & Compliance" />
          <span className="title">{client?.name ?? clientId}</span>
          {client && <span className={`pill ${client.status}`}>{client.status}</span>}
        </div>
      </header>

      <main>
        {client && <ClientSummary client={client} />}

        <section className="register-results">
          <div className="row">
            <h2>Financial Services Register</h2>
            <span className="spacer" />
            {firm && (
              <a href={firm.url} target="_blank" rel="noreferrer noopener" className="small">
                open on the FCA site
              </a>
            )}
          </div>

          {loading && <p className="muted">Reading the FCA Register…</p>}

          {error && !loading && (
            <div className="register error">
              <p className="small">{error}</p>
            </div>
          )}

          {firm && (
            <>
              <p className="muted small">
                <strong>{firm.name}</strong> · {firm.status} · FRN {firm.frn} · read{" "}
                {formatDateTime(firm.fetched_at)}. Live from the FCA — not stored, and not part
                of your log.
              </p>
              <div className="sections">
                {firm.sections.map((section) => (
                  <article key={section.name} className="section">
                    <h3>
                      {section.name}
                      {section.rows.length > 1 && (
                        <span className="muted small"> ({section.rows.length})</span>
                      )}
                    </h3>
                    {section.rows.length ? (
                      section.rows.map((row, index) => <RegisterValue key={index} value={row} />)
                    ) : (
                      <p className="muted small">{section.note || "none recorded"}</p>
                    )}
                    {section.note && section.rows.length > 0 && (
                      <p className="muted small">{section.note}</p>
                    )}
                  </article>
                ))}
              </div>
            </>
          )}
        </section>
      </main>
    </div>
  );
}

/** Her own record, for context. Read-only here — editing stays on the Clients tab. */
function ClientSummary({ client }: { client: Client }) {
  return (
    <section className="client-summary">
      <h2>Client record</h2>
      <dl className="register-fields">
        {client.website && (
          <div>
            <dt>Website</dt>
            <dd>
              <a href={client.website} target="_blank" rel="noreferrer noopener">
                {client.website}
              </a>
            </dd>
          </div>
        )}
        {client.frn && (
          <div>
            <dt>FRN</dt>
            <dd>{client.frn}</dd>
          </div>
        )}
        {client.updated_at && (
          <div>
            <dt>Last changed</dt>
            <dd>
              {formatDateTime(client.updated_at)} by {client.updated_by}
            </dd>
          </div>
        )}
      </dl>
      {client.notes && <p className="summary">{client.notes}</p>}
      {client.scope_profile && (
        <details className="scope-details">
          <summary>Scope profile</summary>
          <pre className="scope">{client.scope_profile}</pre>
        </details>
      )}
    </section>
  );
}

/**
 * True for values worth no space: blanks, and the API's own machine URLs.
 *
 * The firm record carries a link to every sub-resource — Names, Individuals, Requirements,
 * Waivers, Exclusions, DisciplinaryHistory and more — which put eleven rows of
 * `https://register.fca.org.uk/services/V0.1/...` above the first real field. They are
 * plumbing for a program, not information for her, and the ones that matter are already
 * fetched and shown as sections of their own. Matched on the value rather than the key,
 * because the keys are the resource names and there is no pattern to them.
 */
const API_LINK = "register.fca.org.uk/services/";

function isNoise(_key: string, value: unknown): boolean {
  if (value === null || value === undefined || value === "") return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === "string" && value.includes(API_LINK)) return true;
  return false;
}

/**
 * Whatever the Register returned, rendered by shape rather than by name.
 *
 * The fields differ between kinds of firm, so a hand-written layout would silently drop
 * whatever it had not been told to expect — which for an authorisation is the one failure
 * mode worth designing against.
 */
export function RegisterValue({ value }: { value: unknown }) {
  if (Array.isArray(value)) {
    return (
      <ul className="register-list">
        {value.map((entry, index) => (
          <li key={index}>
            <RegisterValue value={entry} />
          </li>
        ))}
      </ul>
    );
  }

  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>).filter(
      ([key, entry]) => !isNoise(key, entry),
    );
    if (!entries.length) return null;
    return (
      <dl className="register-fields">
        {entries.map(([key, entry]) => (
          <div key={key}>
            <dt>{key}</dt>
            <dd>
              <RegisterValue value={entry} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }

  return <>{String(value)}</>;
}
